/**
 * One inline citation, as it appears inside a sentence.
 *
 * The phone's shape (`backend/src/frontend/pages/ask/AnswerBody.tsx`): a kind
 * dot, a 9px uppercase mono label, on the kind's soft ground. `verticalAlign`
 * lifts it off the baseline so a line of text with three chips in it still
 * has an even rhythm.
 *
 * It is an `<a href>` only when it has somewhere to go — the console's
 * universal convention for a record row (see `SourcesCard`, `ItemList`,
 * `AllWalks`): a real `href` plus a `preventDefault`ed `onClick`, so a reader
 * can middle-click or cmd-click a citation into a new tab exactly as they can
 * any other row.
 *
 * When it is a `<span>` instead, there are two genuinely different reasons,
 * and they must not look the same:
 *
 * - **No destination.** `target` is `null` — `citationTarget` could not map
 *   this reference (an unmappable `recordKind`, or a walk citation with no
 *   `visitId`). The citation IS degraded, so it reads that way: muted, the
 *   same `opacity: 0.55` convention as `SourceRow`'s inert row, plus
 *   `aria-disabled="true"` so a screen-reader user gets the same signal a
 *   sighted user gets from the opacity.
 * - **Not interactive in this surface.** `target` resolved fine, but the
 *   caller (the Ask bubble) supplied no `onOpen`/`href` at all — its chips
 *   are formatting, and the way onward is its own "Continue in Ask Led AI"
 *   button. Nothing is wrong here, so the chip renders at full strength with
 *   no ARIA state — muting it would tell the reader something is broken when
 *   nothing is.
 */

import { META, citePalette } from "../chrome";
import type { CitationTarget } from "./citationTarget";

export function CiteChip({
  recordKind,
  label,
  target,
  href,
  onOpen,
}: {
  recordKind: string;
  label: string;
  target: CitationTarget | null;
  /** The console URL for `target`, built by the caller from `citationTarget`
   *  plus its own `hrefForItem`/`hrefForWalk` — see `AnswerBody`'s `hrefFor`.
   *  Absent (as from the Ask bubble) keeps the chip an inert `<span>` even
   *  when `onOpen` is supplied. */
  href?: string;
  onOpen?: ((target: CitationTarget) => void) | undefined;
}) {
  const palette = citePalette(recordKind);

  const body = (
    <>
      <span
        aria-hidden
        style={{
          width: 5,
          height: 5,
          borderRadius: 3,
          background: palette.ink,
          flexShrink: 0,
        }}
      />
      {label}
    </>
  );

  const style: React.CSSProperties = {
    ...META,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 7px",
    margin: "0 1px",
    borderRadius: 6,
    fontSize: 10.35,
    letterSpacing: "0.5px",
    color: palette.ink,
    background: palette.bg,
    verticalAlign: "1px",
  };

  if (!target) {
    // No destination — degraded, and it must read that way.
    return (
      <span
        data-testid="ask-cite"
        aria-disabled="true"
        style={{ ...style, opacity: 0.55 }}
      >
        {body}
      </span>
    );
  }

  if (!onOpen || !href) {
    // Has a destination, just not wired up in this surface (the Ask
    // bubble). Working as intended — full strength, no ARIA state.
    return (
      <span data-testid="ask-cite" style={style}>
        {body}
      </span>
    );
  }

  return (
    <a
      data-testid="ask-cite"
      className="kb"
      href={href}
      onClick={(event) => {
        event.preventDefault();
        onOpen(target);
      }}
      style={{ ...style, cursor: "pointer" }}
    >
      {body}
    </a>
  );
}
